/**
 * Erro de API tipado como classe (não objeto solto): satisfaz `only-throw-error` e permite
 * narrowing seguro via `instanceof` no `catch`, sem `as` em C4. A extração de mensagem preserva
 * o contrato de fato do backend (Middlewares/ExceptionHandler.cs de cada API): tenta, na ordem,
 * `title` (ProblemDetails/binding), `message`, `error` (handler de domínio customizado); cai num
 * mapa fixo PT-BR por status se nada bater.
 */
export type ApiErrorKind = 'http' | 'network' | 'timeout' | 'cors';

export class ApiError extends Error {
  readonly status: number;
  readonly correlationId: string | undefined;
  readonly kind: ApiErrorKind;
  readonly retryAfterMs: number | undefined;

  constructor(params: { status: number; correlationId: string | undefined; kind: ApiErrorKind; message: string; retryAfterMs?: number | undefined }) {
    super(params.message);
    this.name = 'ApiError';
    this.status = params.status;
    this.correlationId = params.correlationId;
    this.kind = params.kind;
    this.retryAfterMs = params.retryAfterMs;
  }
}

const STATUS_FALLBACK: Record<number, string> = {
  400: 'Não foi possível concluir: verifique os dados informados.',
  401: 'Sua sessão expirou. Entre novamente.',
  403: 'Você não tem permissão para esta ação.',
  404: 'Não encontramos o que você está procurando.',
  409: 'Esta ação não pode ser concluída agora — os dados mudaram.',
  500: 'Algo deu errado do nosso lado. Tente novamente em instantes.',
};

const TECHNICAL_ERROR_PATTERN =
  /(?:Exception|System\.|Microsoft\.|at\s+[\w.]+\(|Npgsql|Postgres|NullReference|SqlException|Connection\s+refused|ECONNREFUSED|TimeoutException|HttpRequestException|Stack\s*Trace)/i;

interface ErrorBody {
  title?: unknown;
  message?: unknown;
  error?: unknown;
}

function isErrorBody(value: unknown): value is ErrorBody {
  return typeof value === 'object' && value !== null;
}

export function sanitizeErrorMessage(message: string, fallback: string): string {
  return TECHNICAL_ERROR_PATTERN.test(message) ? fallback : message;
}

export function extractErrorMessage(status: number, body: unknown): string {
  if (status >= 500) return STATUS_FALLBACK[status] ?? 'Algo deu errado do nosso lado. Tente novamente em instantes.';

  if (isErrorBody(body)) {
    for (const key of ['title', 'message', 'error'] as const) {
      const candidate = body[key];
      if (typeof candidate === 'string' && candidate.trim().length > 0) {
        return sanitizeErrorMessage(candidate.trim(), STATUS_FALLBACK[status] ?? 'Não foi possível concluir a operação.');
      }
    }
  }
  return STATUS_FALLBACK[status] ?? `Erro inesperado (${String(status)}).`;
}
